import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type StartBody =
  paths["/api/v1/contracts/{contract_id}/schedule"]["post"]["requestBody"]["content"]["application/json"];
type UpdateBody =
  paths["/api/v1/contracts/{contract_id}/schedule"]["patch"]["requestBody"]["content"]["application/json"];

const key = (id: string) => ["contracts", id, "billing"] as const;

export function useChargeLeasingFee(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/leasing-fee",
        { params: { path: { contract_id: contractId } } },
      );
      if (error || !data) throw new Error("Failed to charge leasing fee");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
      qc.invalidateQueries({ queryKey: ["contracts", contractId] });
    },
  });
}

export function useContractBilling(contractId: string) {
  return useQuery({
    queryKey: key(contractId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/contracts/{contract_id}/schedule",
        { params: { path: { contract_id: contractId } } },
      );
      if (error || !data) throw new Error("Failed to load billing");
      return data;
    },
  });
}

export function useStartBilling(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StartBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/schedule",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to start billing");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(contractId) }),
  });
}

export function useUpdateBilling(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBody) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/contracts/{contract_id}/schedule",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to update billing");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(contractId) }),
  });
}
